import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Network, { BaseURL } from "../../utils/Network";
import MidBox from "../../components/Boxes/MidBox";
import Button from "../../components/Button";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import { isMobile } from "react-device-detect";
import { useSelector } from "react-redux";
import DOMPurify from "dompurify";
import classNames from "classnames";
import {
  GameAnnouncements,
  GameUpdate,
  GameUpdateCategory,
  GameUpdatesPage,
} from "../../types/global";
import Dialog from "../../components/Dialog";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import "./GameUpdates.css";

const PAGE_SIZE = 10;

const CATEGORIES: Record<number, { key: string; icon: string }> = {
  [GameUpdateCategory.NewFeature]: { key: "newFeature", icon: "/assets/images/icons/star.svg" },
  [GameUpdateCategory.Improvement]: { key: "improvement", icon: "/assets/images/icons/upgrade.png" },
  [GameUpdateCategory.Balance]: { key: "balance", icon: "/assets/images/icons/setting.png" },
  [GameUpdateCategory.Fixes]: { key: "fixes", icon: "/assets/images/icons/repair.png" },
  [GameUpdateCategory.Event]: { key: "event", icon: "/assets/images/icons/crown-flag.png" },
};
const DEFAULT_ICON = "/assets/images/icons/updates.png";

// Release notes are written by the team, but they are still HTML from the server: only text
// formatting, lists, links and pictures get through.
const SANITIZE = {
  ALLOWED_TAGS: ["p", "br", "ul", "ol", "li", "b", "strong", "i", "em", "u", "a", "img", "h3", "h4", "span"],
  ALLOWED_ATTR: ["href", "target", "rel", "src", "alt", "title"],
};

function byNewest(a: GameUpdate, b: GameUpdate) {
  return new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime() || b.id - a.id;
}

const GameUpdates = () => {
  const { t, currentLanguage } = useAppTranslation(["learn"]);
  const { gameUpdates: announced }: GameAnnouncements = useSelector(
    (state: any) => state.announcements
  );
  const [items, setItems] = useState<GameUpdate[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState<GameUpdate | undefined>();
  // Answers to an older request (another page, the language before) are dropped.
  const requestId = useRef(0);

  const dateFormat = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(currentLanguage, { year: "numeric", month: "long", day: "numeric" });
    } catch {
      return new Intl.DateTimeFormat("en", { year: "numeric", month: "long", day: "numeric" });
    }
  }, [currentLanguage]);

  const load = useCallback(
    async (nextPage: number) => {
      const id = ++requestId.current;
      nextPage === 1 ? setLoading(true) : setLoadingMore(true);
      const response = await new Network().CallApi<GameUpdatesPage>({
        url: `/api/v2/announcement/getgameupdates?page=${nextPage}&count=${PAGE_SIZE}`,
        method: "GET",
        customHost: BaseURL,
      });
      if (id !== requestId.current) return;

      if (response.ok && response.data) {
        const result = response.data.result || [];
        setItems((current) => (nextPage === 1 ? result : [...current, ...result]));
        setTotal(response.data.count || 0);
        setPage(nextPage);
        setFailed(false);
      } else if (nextPage === 1) {
        setItems([]);
        setTotal(0);
        setFailed(true);
      }
      setLoading(false);
      setLoadingMore(false);
    },
    []
  );

  // The server writes the texts in the chosen language, so they are read again when it changes.
  useEffect(() => {
    load(1);
  }, [load, currentLanguage]);

  // A server without the release-notes call still sends the latest updates with the announcements.
  const fallback = useMemo(
    () => (announced || []).filter((item) => item?.isActive).sort(byNewest),
    [announced]
  );
  const shown = failed && fallback.length > 0 ? fallback : items;
  const canLoadMore = !failed && items.length < total;

  const formatDate = (value?: string) => {
    if (!value) return "";
    const date = new Date(value);
    return isNaN(date.getTime()) ? "" : dateFormat.format(date);
  };

  const renderMeta = (item: GameUpdate) => {
    const category = item.category ? CATEGORIES[item.category] : undefined;
    return (
      <div className="game-update-meta">
        {item.date && <span className="game-update-date">{formatDate(item.date)}</span>}
        {item.version && (
          <span className="game-update-chip is-version">
            {t("learn:gameUpdates.version", { version: item.version })}
          </span>
        )}
        {category && (
          <span className={classNames("game-update-chip", "is-" + category.key)}>
            {t(`learn:gameUpdates.categories.${category.key}`)}
          </span>
        )}
      </div>
    );
  };

  const iconOf = (item: GameUpdate) =>
    item.iconUrl || (item.category ? CATEGORIES[item.category]?.icon : undefined) || DEFAULT_ICON;

  return (
    <SkeletonTheme enableAnimation={!isMobile} baseColor="#3e382b" highlightColor="#474132">
      <div>
        <MidBox modal className="mt-5" title={t("learn:gameUpdates.title")}>
          <div className="game-updates pt-3 pb-5 px-1 px-sm-3">
            {loading &&
              [1, 2, 3].map((key) => (
                <div key={"loading" + key} className="game-update-item is-placeholder">
                  <Skeleton width={44} height={44} circle />
                  <div className="game-update-text">
                    <Skeleton width={"40%"} height={20} />
                    <Skeleton width={"25%"} />
                    <Skeleton width={"90%"} />
                  </div>
                </div>
              ))}

            {!loading && shown.length === 0 && (
              <div className="game-update-state dark-yellow">
                <div className="fs-big">
                  {failed ? t("learn:gameUpdates.error") : t("learn:gameUpdates.empty")}
                </div>
                {failed && (
                  <Button variant="primary" className="mx-auto mt-3" onClick={() => load(1)}>
                    {t("learn:gameUpdates.retry")}
                  </Button>
                )}
              </div>
            )}

            {!loading &&
              shown.map((item) => (
                <button
                  type="button"
                  key={item.id}
                  className="game-update-item"
                  onClick={() => setOpen(item)}
                >
                  <img className="game-update-icon" src={iconOf(item)} alt="" loading="lazy" />
                  <div className="game-update-text">
                    <div className="game-update-title fs-lg">{item.title}</div>
                    {renderMeta(item)}
                    {item.description && <div className="game-update-summary">{item.description}</div>}
                  </div>
                  <span className="game-update-more">{t("learn:gameUpdates.readMore")}</span>
                </button>
              ))}

            {!loading && canLoadMore && (
              <div className="d-flex justify-content-center mt-3">
                <Button variant="wide" loading={loadingMore} onClick={() => load(page + 1)}>
                  {t("learn:gameUpdates.loadMore")}
                </Button>
              </div>
            )}
          </div>
        </MidBox>

        <Dialog
          open={!!open}
          onClose={() => setOpen(undefined)}
          title={
            <>
              <img src={open ? iconOf(open) : DEFAULT_ICON} width={22} className="me-2" alt="" />
              {open?.title}
            </>
          }
          size="lg"
          backdropClose
        >
          {open && (
            <div className="game-update-detail">
              {renderMeta(open)}
              {open.html ? (
                <div
                  className="game-update-body"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(open.html, SANITIZE) }}
                />
              ) : (
                <p className="game-update-body">{open.description}</p>
              )}
            </div>
          )}
        </Dialog>
      </div>
    </SkeletonTheme>
  );
};

export default GameUpdates;
